import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Candidate } from "../../types";
import apiService from "../../services/api";
import Tooltip from "../../components/Tooltip";
import VerifiedBadge from "../../components/VerifiedBadge";
import { formatMonthYear } from "../../utils/datetime";
import { formatAvailability } from "../../utils/displayFormatters";

const formatPhoneWithCountryCode = (countryCode?: string, phone?: string) => {
  const cleanPhone = phone?.trim();
  const cleanCountryCode = countryCode?.trim();

  if (!cleanPhone) return "";
  if (!cleanCountryCode || cleanPhone.startsWith("+")) return cleanPhone;

  return `${cleanCountryCode} ${cleanPhone}`;
};

const CandidateProfileView: React.FC = () => {
  const navigate = useNavigate();
  const [candidate, setCandidate] = useState<Candidate | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<
    | "overview"
    | "experience"
    | "education"
    | "skills"
    | "projects"
    | "certifications"
    | "additional"
    | "resumes"
  >("overview");

  useEffect(() => {
    loadCandidateProfile();
  }, []);

  const loadCandidateProfile = async (): Promise<void> => {
    try {
      setLoading(true);
      setError(null);

      const data = await apiService.getCandidateProfile();
      setCandidate(data);
    } catch (err: any) {
      console.error("Error loading candidate profile:", err);
      setError(err.message || "Failed to load profile");
    } finally {
      setLoading(false);
    }
  };

  const handleEditProfile = () => {
    navigate("/candidate/profile/edit");
  };

  const displayPhone = formatPhoneWithCountryCode(
    candidate?.country_code,
    candidate?.phone,
  );

  const handlePublicProfile = async () => {
    try {
      if (candidate?.public_profile_slug) {
        // Open existing public profile in new tab
        window.open(`/profile/${candidate.public_profile_slug}`, "_blank");
      } else {
        // Generate public profile slug first
        setLoading(true);
        const result = await apiService.generatePublicProfileLink();

        if (result.success && result.slug) {
          // Update candidate state with new slug
          setCandidate((prev) =>
            prev
              ? {
                ...prev,
                public_profile_slug: result.slug,
                public_profile_enabled: true,
                public_profile_url: result.public_url,
              }
              : null,
          );

          // Open the newly generated public profile
          window.open(`/profile/${result.slug}`, "_blank");
        } else {
          setError("Failed to generate public profile. Please try again.");
        }
      }
    } catch (error: any) {
      console.error("Error handling public profile:", error);
      setError("Failed to open public profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center mb-6">
              <div className="h-20 w-20 bg-gray-200 rounded-full mr-6"></div>
              <div>
                <div className="h-6 bg-gray-200 rounded w-48 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-32 mb-2"></div>
                <div className="h-4 bg-gray-200 rounded w-40"></div>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="h-32 bg-gray-200 rounded"></div>
              <div className="h-32 bg-gray-200 rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !candidate) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          <div className="flex items-start">
            <svg
              className="h-5 w-5 text-red-400 mr-2 mt-0.5"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
            <div className="flex-1">
              <p className="font-medium">Unable to load your profile</p>
              <p className="text-sm mt-1">{error || "Profile not found"}</p>
              <div className="mt-3">
                <button
                  onClick={() => navigate("/candidate/onboarding")}
                  className="bg-red-100 hover:bg-red-200 text-red-800 px-3 py-1 rounded text-sm font-medium"
                >
                  Create Profile
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: "overview", name: "Overview", icon: "👤" },
    { id: "experience", name: "Experience", icon: "💼" },
    { id: "education", name: "Education", icon: "🎓" },
    { id: "skills", name: "Skills", icon: "⚡" },
    { id: "projects", name: "Projects", icon: "🚀" },
    { id: "certifications", name: "Certifications", icon: "🏆" },
    { id: "additional", name: "Languages & Awards", icon: "🌟" },
    // { id: "resumes", name: "Resumes", icon: "📄" },
  ];

  const availability = candidate.availability?.trim() ?? "";
  const preferredWorkType = candidate.preferred_work_type?.trim() ?? "";
  const workAuthorization = candidate.work_authorization?.trim() ?? "";
  const expectedSalary = candidate.expected_salary;
  const isVerified =
    candidate.is_identity_verified === true || candidate.is_id_verified === true;
  const hasAvailability = availability.length > 0;
  const hasPreferredWorkType = preferredWorkType.length > 0;
  const hasExpectedSalary =
    typeof expectedSalary === "number" && expectedSalary > 0;
  const hasWorkAuthorization = workAuthorization.length > 0;
  const hasWillingToRelocate =
    candidate.willing_to_relocate !== undefined &&
    candidate.willing_to_relocate !== null;
  const hasWorkPreferences =
    hasAvailability ||
    hasPreferredWorkType ||
    hasExpectedSalary ||
    hasWorkAuthorization ||
    hasWillingToRelocate;

  const compareCandidateProjects = (firstProject: any, secondProject: any): number => {
    const firstProjectIsOngoing = firstProject?.is_ongoing === true;
    const secondProjectIsOngoing = secondProject?.is_ongoing === true;

    if (firstProjectIsOngoing && !secondProjectIsOngoing) return -1;
    if (!firstProjectIsOngoing && secondProjectIsOngoing) return 1;

    const resolveProjectSortDate = (project: any): Date | null => {
      if (!project || typeof project !== "object") return null;
      const dateValue = project.is_ongoing ? project.start_date : project.end_date;
      return dateValue ? new Date(dateValue) : null;
    };

    const firstProjectDate = resolveProjectSortDate(firstProject);
    const secondProjectDate = resolveProjectSortDate(secondProject);

    if (firstProjectDate && secondProjectDate) {
      return secondProjectDate.getTime() - firstProjectDate.getTime();
    }
    if (firstProjectDate) return -1;
    if (secondProjectDate) return 1;
    return 0;
  };

  return (
    <div className="p-3 sm:p-6 max-w-7xl mx-auto">
      {/* Header with Edit Button */}
      <div className="bg-white rounded-lg shadow-sm border p-4 sm:p-6 mb-4 sm:mb-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center">
            {/* Avatar */}
            <div className="flex-shrink-0 h-14 w-14 sm:h-20 sm:w-20 mx-auto sm:mx-0">
              <div className="h-14 w-14 sm:h-20 sm:w-20 rounded-full bg-emerald-100 flex items-center justify-center">
                <span className="text-emerald-600 font-bold text-base sm:text-2xl">
                  {candidate.first_name.charAt(0)}
                  {candidate.last_name.charAt(0)}
                </span>
              </div>
            </div>

            {/* Info Section */}
            <div className="sm:ml-6 text-center sm:text-left mt-3 sm:mt-0 w-full">
              {/* Name */}
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                <h1 className="text-lg sm:text-2xl font-bold text-gray-900 break-words">
                  {candidate.first_name} {candidate.last_name}
                </h1>
                {isVerified && <VerifiedBadge />}
              </div>

              {/* Job Title */}
              <p className="text-sm sm:text-lg text-gray-600 mt-1 break-words">
                {candidate.current_job_title}
                {candidate.current_company && (
                  <span className="block sm:inline">
                    {" "}at {candidate.current_company}
                  </span>
                )}
              </p>

              {/* Meta Info */}
              <div className="flex flex-wrap justify-center sm:justify-start items-center gap-2 mt-2">
                {candidate.location && (
                  <span className="text-xs sm:text-sm text-gray-500 flex items-center">
                    <svg
                      className="w-4 h-4 mr-1"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    {candidate.location}
                  </span>
                )}

                {candidate.years_of_experience && (
                  <span className="text-xs sm:text-sm text-gray-500">
                    {candidate.years_of_experience} years experience
                  </span>
                )}

                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-100 text-emerald-800">
                  Your Profile
                </span>
              </div>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 w-full sm:w-auto">
            <button
              onClick={handleEditProfile}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 w-full sm:w-auto"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z" />
              </svg>
              Edit Profile
            </button>
            <button
              onClick={handlePublicProfile}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-emerald-600 hover:bg-emerald-700 w-full sm:w-auto"
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M5 3a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2V5a2 2 0 00-2-2H5zm0 2h10v7h-2l-1-2H8L7 12H5V5z"
                  clipRule="evenodd"
                />
              </svg>
              Public Profile
            </button>
          </div>
        </div>

        {/* Profile Completeness Indicator */}
        <div className="mt-4 sm:mt-6 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="text-center sm:text-left">
              <h3 className="text-sm font-medium text-blue-900">
                How Companies See You
              </h3>
              <p className="text-sm text-blue-700 mt-1">
                This is exactly how your profile appears to recruiters and
                hiring managers.
              </p>
            </div>
            <div className="flex items-center justify-center sm:justify-end">
              <div className="text-center sm:text-right">
                <div className="text-xl sm:text-2xl font-bold text-blue-900">
                  {candidate.resume_completeness_score
                    ? Math.round(candidate.resume_completeness_score * 100)
                    : 85}
                  %
                </div>
                <div className="text-xs text-blue-600">Complete</div>
              </div>
              <div className="ml-2 relative">
                <Tooltip
                  content="Basic Info & Education: 35%
Skills & Experience: 35%
Professional Summary: 15%
Extras (Projects, Certifications & Languages): +15%"
                  position="left"
                >
                  <svg
                    className="w-4 h-4 text-blue-600 cursor-help"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-8-3a1 1 0 00-.867.5 1 1 0 11-1.731-1A3 3 0 0113 8a3.001 3.001 0 01-2 2.83V11a1 1 0 11-2 0v-1a1 1 0 011-1 1 1 0 100-2zm0 8a1 1 0 100-2 1 1 0 000 2z"
                      clipRule="evenodd"
                    />
                  </svg>
                </Tooltip>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="border-b border-gray-200">
          <div className="overflow-x-auto">
            <nav className="-mb-px flex space-x-1 sm:space-x-4 px-2 sm:px-6 min-w-max">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`py-3 sm:py-4 px-2 sm:px-3 border-b-2 font-medium text-xs sm:text-sm whitespace-nowrap min-h-[44px] flex items-center justify-center flex-shrink-0 ${activeTab === tab.id
                    ? "border-emerald-500 text-emerald-600"
                    : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                >
                  <span className="mr-1 text-sm">{tab.icon}</span>
                  <span className="hidden sm:inline">{tab.name}</span>
                  <span className="sm:hidden text-xs">
                    {tab.name.split(" ")[0]}
                  </span>
                </button>
              ))}
            </nav>
          </div>
        </div>

        <div className="p-3 sm:p-6">
          {/* Overview Tab */}
          {activeTab === "overview" && (
            <div className="space-y-4 sm:space-y-6">
              {/* Contact Information */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
                    Contact Information
                  </h3>
                  <dl className="space-y-2 sm:space-y-3">
                    <div>
                      <dt className="text-sm font-medium text-gray-500">
                        Email
                      </dt>
                      <dd className="text-sm text-gray-900 break-all">
                        {candidate.email}
                      </dd>
                    </div>
                    {displayPhone && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          Phone
                        </dt>
                        <dd className="text-sm text-gray-900">
                          {displayPhone}
                        </dd>
                      </div>
                    )}
                    {candidate.linkedin_url && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          LinkedIn
                        </dt>
                        <dd className="text-sm text-gray-900">
                          <a
                            href={candidate.linkedin_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:text-emerald-500 break-all"
                          >
                            View Profile
                          </a>
                        </dd>
                      </div>
                    )}
                    {candidate.github_url && (
                      <div>
                        <dt className="text-sm font-medium text-gray-500">
                          GitHub
                        </dt>
                        <dd className="text-sm text-gray-900">
                          <a
                            href={candidate.github_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-emerald-600 hover:text-emerald-500 break-all"
                          >
                            View Profile
                          </a>
                        </dd>
                      </div>
                    )}
                    {/* Work Preferences */}
                    {hasWorkPreferences && (
                      <div className="mt-6">
                          <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
                            Work Preferences
                          </h3>

                          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            {hasAvailability && (
                              <div>
                                <dt className="text-sm font-medium text-gray-500">
                                  Availability
                                </dt>
                                <dd className="text-sm text-gray-900">
                                  {formatAvailability(availability)}
                                </dd>
                              </div>
                            )}

                            {hasPreferredWorkType && (
                              <div>
                                <dt className="text-sm font-medium text-gray-500">
                                  Preferred Work Type
                                </dt>
                                <dd className="text-sm text-gray-900 capitalize">
                                  {preferredWorkType}
                                </dd>
                              </div>
                            )}

                            {hasExpectedSalary && (
                              <div>
                                <dt className="text-sm font-medium text-gray-500">
                                  Expected Salary
                                </dt>
                                <dd className="text-sm text-gray-900">
                                  ${expectedSalary.toLocaleString()}
                                </dd>
                              </div>
                            )}

                            {hasWorkAuthorization && (
                              <div>
                                <dt className="text-sm font-medium text-gray-500">
                                  Work Authorization
                                </dt>
                                <dd className="text-sm text-gray-900 capitalize">
                                  {workAuthorization}
                                </dd>
                              </div>
                            )}

                            {hasWillingToRelocate && (
                              <div>
                                <dt className="text-sm font-medium text-gray-500">
                                  Willing to Relocate
                                </dt>
                                <dd className="text-sm text-gray-900">
                                  {candidate.willing_to_relocate ? "Yes" : "No"}
                                </dd>
                              </div>
                            )}
                          </dl>
                      </div>
                    )}
                  </dl>
                </div>

                {candidate.professional_summary && (
                  <div>
                    <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
                      Professional Summary
                    </h3>
                    <div className="prose prose-sm max-w-none">
                      <p className="text-sm text-gray-700 leading-relaxed break-words">
                        {candidate.professional_summary}
                      </p>
                    </div>
                  </div>
                )}
              </div>

              {/* Skills */}
              {candidate.skills && candidate.skills.length > 0 ? (
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-3 sm:mb-4">
                    Skills
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
                    {candidate.skills.map((skill, index) => (
                      <div
                        key={index}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                      >
                        <div className="flex-1 min-w-0 pr-2">
                          <p
                            className="text-sm font-medium text-gray-900 truncate"
                            title={skill.skill_name}
                          >
                            {skill.skill_name}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {skill.proficiency_level}
                            {skill.years_of_experience &&
                              ` • ${skill.years_of_experience} years`}
                          </p>
                        </div>
                        {skill.confidence_score && (
                          <div className="text-right flex-shrink-0">
                            <div className="text-xs text-gray-500">
                              Confidence
                            </div>
                            <div className="text-sm font-medium text-emerald-600">
                              {Math.round(skill.confidence_score * 100)}%
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-6 sm:p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No skills listed yet
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 px-2">
                    Add your technical and soft skills to attract the right
                    opportunities.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                    >
                      Add Skills
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Experience Tab */}
          {activeTab === "experience" && (
            <div className="space-y-4 sm:space-y-6">
              {(candidate.experiences || candidate.work_experience) &&
                (candidate.experiences || candidate.work_experience)!.length >
                0 ? (
                <div>
                  <h3 className="text-base sm:text-lg font-medium text-gray-900 mb-4 sm:mb-6">
                    Work Experience
                  </h3>
                  <div className="space-y-4 sm:space-y-6">
                    {(
                      candidate.experiences ||
                      candidate.work_experience ||
                      []
                    ).map((experience, index) => (
                      <div
                        key={experience.id || index}
                        className="border-l-4 border-emerald-500 pl-4 sm:pl-6 pb-4 sm:pb-6"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                          <div className="flex-1 min-w-0">
                            <h4
                              className="text-base sm:text-lg font-semibold text-gray-900 break-words"
                              title={experience.job_title}
                            >
                              {experience.job_title}
                            </h4>
                            <p
                              className="text-sm sm:text-base text-emerald-600 font-medium break-words"
                              title={experience.company_name}
                            >
                              {experience.company_name}
                            </p>
                            {experience.location && (
                              <p className="text-sm text-gray-600 break-words">
                                {experience.location}
                              </p>
                            )}
                            <div className="flex flex-col sm:flex-row sm:items-center mt-2 gap-1 sm:gap-2">
                              <span className="text-sm text-gray-500">
                                {experience.start_date &&
                                  formatMonthYear(experience.start_date)}{" "}

                                {experience.is_current
                                  ? " Present"
                                  : experience.end_date &&
                                  ` ${formatMonthYear(experience.end_date)}`}
                              </span>
                              {experience.is_current && (
                                <span className="px-2 py-1 text-xs bg-emerald-100 text-emerald-800 rounded-full self-start">
                                  Current
                                </span>
                              )}
                            </div>
                            {experience?.employment_type && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-800 mt-2">
                                {experience.employment_type.replace('_', ' ').toUpperCase()}
                              </span>
                            )}
                            {experience.description && (
                              <div className="mt-3">
                                <p className="text-sm text-gray-700 break-words leading-relaxed">
                                  {experience.description}
                                </p>
                              </div>
                            )}
                            {experience.responsibilities &&
                              experience.responsibilities.length > 0 && (
                                <div className="mt-3">
                                  <h5 className="text-sm font-medium text-gray-900 mb-2">
                                    Key Responsibilities:
                                  </h5>
                                  <ul className="list-disc list-inside space-y-1 pl-2">
                                    {experience.responsibilities.map(
                                      (responsibility, respIndex) => (
                                        <li
                                          key={respIndex}
                                          className="text-sm text-gray-700 break-words leading-relaxed"
                                        >
                                          {responsibility}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}
                            {experience.achievements &&
                              experience.achievements.length > 0 && (
                                <div className="mt-3">
                                  <h5 className="text-sm font-medium text-gray-900 mb-2">
                                    Key Achievements:
                                  </h5>
                                  <ul className="list-disc list-inside space-y-1 pl-2">
                                    {experience.achievements.map(
                                      (achievement, achIndex) => (
                                        <li
                                          key={achIndex}
                                          className="text-sm text-gray-700 break-words leading-relaxed"
                                        >
                                          {achievement}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}
                            {experience?.technologies_used &&
                              experience.technologies_used.length > 0 && (
                                <div className="mt-3">
                                  <p className="text-sm font-medium text-gray-900 mb-2">
                                    Technologies Used:
                                  </p>
                                  <div className="flex flex-wrap gap-2">
                                    {experience.technologies_used.map((tech, i) => (
                                      <span
                                        key={i}
                                        className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                      >
                                        {tech}
                                      </span>
                                    ))}
                                  </div>
                                </div>
                              )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-6 sm:p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2-2v2m8 0V6a2 2 0 00-2 2H8a2 2 0 00-2-2V6m8 0h2a2 2 0 012 2v6a2 2 0 01-2 2H6a2 2 0 01-2-2V8a2 2 0 012-2h2"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No work experience listed
                  </h3>
                  <p className="mt-1 text-sm text-gray-500 px-2">
                    Add your work experience to showcase your professional
                    journey.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                    >
                      Add Work Experience
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Education Tab */}
          {activeTab === "education" && (
            <div className="space-y-6">
              {candidate.education && candidate.education.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Education
                  </h3>
                  <div className="space-y-4">
                    {candidate.education.map((edu, index) => (
                      <div
                        key={edu.id || index}
                        className="border-l-4 border-blue-500 pl-6 pb-4"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <h4 className="text-lg font-medium text-gray-900">
                              {edu.degree_name ||
                                `${edu.degree_type} in ${edu.field_of_study}`}
                            </h4>
                            <p className="text-md text-green-600 font-medium">
                              {edu.institution_name}
                            </p>
                            <p className="text-sm text-gray-500 mt-1">
                              {edu.graduation_year &&
                                `Graduated: ${edu.graduation_year}`}
                              {edu.start_date && edu.end_date ? (
                                ` • ${new Date(
                                  edu.start_date,
                                ).getFullYear()} - ${new Date(
                                  edu.end_date,
                                ).getFullYear()}`
                              ) : (
                                <span className="text-gray-400 text-xs">
                                  {" "}
                                  • No date info
                                </span>
                              )}
                              {edu.is_current && " • Currently Enrolled"}
                            </p>
                            {edu.major && (
                              <p className="text-sm text-gray-600 mt-1">
                                Major: {edu.major}
                              </p>
                            )}
                            {edu.minor && (
                              <p className="text-sm text-gray-600">
                                Minor: {edu.minor}
                              </p>
                            )}
                          </div>
                        </div>
                        {edu.gpa && (
                          <span className="text-sm text-gray-500">
                            GPA: {edu.gpa}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No education listed
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add your educational background to strengthen your profile.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                    >
                      Add Education
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Skills Tab */}
          {activeTab === "skills" && (
            <div className="space-y-6">
              {candidate.skills && candidate.skills.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Technical & Professional Skills
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {candidate.skills.map((skill, index) => (
                      <div
                        key={index}
                        className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <h4 className="text-sm font-semibold text-indigo-900">
                            {skill.skill_name}
                          </h4>
                          {skill.confidence_score && (
                            <span className="text-xs text-indigo-600 bg-indigo-100 px-2 py-1 rounded">
                              {Math.round(skill.confidence_score * 100)}%
                            </span>
                          )}
                        </div>
                        <div className="space-y-1">
                          {skill.proficiency_level && (
                            <p className="text-xs text-indigo-700">
                              <span className="font-medium">Level:</span>{" "}
                              {skill.proficiency_level}
                            </p>
                          )}
                          {skill.years_of_experience && (
                            <p className="text-xs text-indigo-700">
                              <span className="font-medium">Experience:</span>{" "}
                              {skill.years_of_experience} years
                            </p>
                          )}
                          {skill.context && (
                            <p className="text-xs text-indigo-600">
                              {skill.context}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 10V3L4 14h7v7l9-11h-7z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No skills listed
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add skills to showcase your expertise and attract the right
                    opportunities.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                    >
                      Add Skills
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Projects Tab */}
          {activeTab === "projects" && (
            <div className="space-y-6">
              {candidate.projects && candidate.projects.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Projects & Portfolio
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {candidate.projects
                      .slice()
                      .sort(compareCandidateProjects)
                      .map((project, index) => (
                      <div
                        key={index}
                        className="p-6 bg-gray-50 rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {typeof project === "string"
                              ? project
                              : project.name}
                          </h4>
                          {typeof project === "object" && project.url && (
                            <a
                              href={project.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-emerald-600 hover:text-emerald-500"
                            >
                              <svg
                                className="w-5 h-5"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                />
                              </svg>
                            </a>
                          )}
                        </div>

                        {typeof project === "object" && (
                          <>
                            {project.description && (
                              <p className="text-sm text-gray-700 mb-3">
                                {project.description}
                              </p>
                            )}

                            {project.role && (
                              <p className="text-sm text-gray-600 mb-2">
                                <span className="font-medium">Role:</span>{" "}
                                {project.role}
                              </p>
                            )}

                            {project.technologies &&
                              Array.isArray(project.technologies) &&
                              project.technologies.length > 0 && (
                                <div className="mb-3">
                                  <p className="text-sm font-medium text-gray-700 mb-2">
                                    Technologies:
                                  </p>
                                  <div className="flex flex-wrap gap-1">
                                    {project.technologies.map(
                                      (tech, techIndex) => (
                                        <span
                                          key={techIndex}
                                          className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800"
                                        >
                                          {tech}
                                        </span>
                                      ),
                                    )}
                                  </div>
                                </div>
                              )}

                            {project.achievements &&
                              Array.isArray(project.achievements) &&
                              project.achievements.length > 0 && (
                                <div>
                                  <p className="text-sm font-medium text-gray-700 mb-2">
                                    Key Achievements:
                                  </p>
                                  <ul className="list-disc list-inside space-y-1">
                                    {project.achievements.map(
                                      (achievement, achIndex) => (
                                        <li
                                          key={achIndex}
                                          className="text-sm text-gray-600"
                                        >
                                          {achievement}
                                        </li>
                                      ),
                                    )}
                                  </ul>
                                </div>
                              )}

                            {(project.start_date || project.end_date) && (
                              <div className="mt-3 pt-3 border-t border-gray-200">
                                <p className="text-xs text-gray-500">
                                  {project.start_date && formatMonthYear(project.start_date)}
                                  {project.start_date && (project.end_date || project.is_ongoing) && " - "}
                                  {project.is_ongoing
                                    ? "Ongoing"
                                    : project.end_date
                                      ? new Date(
                                        project.end_date,
                                      ).toLocaleDateString("en-US", {
                                        year: "numeric",
                                        month: "short",
                                      })
                                      : ""}
                                </p>
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No projects listed
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Showcase your work by adding projects to your profile.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                    >
                      Add Projects
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Certifications Tab */}
          {activeTab === "certifications" && (
            <div className="space-y-6">
              {candidate.certifications &&
                candidate.certifications.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Professional Certifications
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {candidate.certifications.map((cert, index) => (
                      <div
                        key={index}
                        className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border border-green-200"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-green-900">
                              {typeof cert === "string" ? cert : cert.name}
                            </h4>
                            {typeof cert === "object" &&
                              cert.issuing_organization && (
                                <p className="text-sm text-green-700 font-medium mt-1">
                                  {cert.issuing_organization}
                                </p>
                              )}
                          </div>
                          <svg
                            className="w-6 h-6 text-green-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                            />
                          </svg>
                        </div>

                        {typeof cert === "object" && (
                          <div className="space-y-2">
                            {cert.issue_date && (
                              <p className="text-sm text-green-700">
                                <span className="font-medium">Issued:</span>{" "}
                                {formatMonthYear(cert.issue_date)}
                              </p>
                            )}

                            {cert.expiration_date && !cert.does_not_expire && (
                              <p className="text-sm text-green-700">
                                <span className="font-medium">Expires:</span>{" "}
                                {formatMonthYear(cert.expiration_date)}
                              </p>
                            )}

                            {cert.does_not_expire && (
                              <p className="text-sm text-green-700">
                                <span className="font-medium">Valid:</span> Does
                                not expire
                              </p>
                            )}

                            {cert.credential_id && (
                              <p className="text-sm text-green-600">
                                <span className="font-medium">ID:</span>{" "}
                                {cert.credential_id}
                              </p>
                            )}

                            {cert.credential_url && (
                              <a
                                href={cert.credential_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center text-sm text-green-600 hover:text-green-500 mt-2"
                              >
                                <svg
                                  className="w-4 h-4 mr-1"
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                                  />
                                </svg>
                                Verify Credential
                              </a>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    No certifications listed
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Add professional certifications to validate your expertise.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                    >
                      Add Certifications
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Additional Tab - Languages & Awards */}
          {activeTab === "additional" && (
            <div className="space-y-8">
              {/* Languages Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Languages
                </h3>
                {candidate.languages && candidate.languages.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {candidate.languages.map((lang, index) => (
                      <div
                        key={index}
                        className="p-4 bg-blue-50 rounded-lg border border-blue-200"
                      >
                        <div className="flex items-center justify-between">
                          <h4 className="text-sm font-semibold text-blue-900">
                            {typeof lang === "string"
                              ? lang
                              : lang.name || lang.language}
                          </h4>
                          {typeof lang === "object" && lang.is_native && (
                            <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                              Native
                            </span>
                          )}
                        </div>
                        {typeof lang === "object" && lang.proficiency && (
                          <p className="text-sm text-blue-700 mt-1 capitalize">
                            {lang.proficiency} proficiency
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-blue-50 rounded-lg border-2 border-dashed border-blue-300">
                    <svg
                      className="mx-auto h-8 w-8 text-blue-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-blue-600">
                      No languages listed
                    </p>
                  </div>
                )}
              </div>

              {/* Awards Section */}
              <div>
                <h3 className="text-lg font-medium text-gray-900 mb-6">
                  Awards & Achievements
                </h3>
                {candidate.awards && candidate.awards.length > 0 ? (
                  <div className="space-y-4">
                    {candidate.awards.map((award, index) => (
                      <div
                        key={index}
                        className="p-4 bg-yellow-50 rounded-lg border border-yellow-200"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <h4 className="text-lg font-semibold text-yellow-900">
                              {typeof award === "string" ? award : award.title}
                            </h4>
                            {typeof award === "object" &&
                              award.organization && (
                                <p className="text-sm text-yellow-700 font-medium mt-1">
                                  {award.organization}
                                </p>
                              )}
                            {typeof award === "object" && award.date && (
                              <p className="text-sm text-yellow-600 mt-1">
                                {new Date(award.date).toLocaleDateString(
                                  "en-US",
                                  {
                                    year: "numeric",
                                    month: "long",
                                  },
                                )}
                              </p>
                            )}
                            {typeof award === "object" && award.description && (
                              <p className="text-sm text-yellow-800 mt-2">
                                {award.description}
                              </p>
                            )}
                          </div>
                          <svg
                            className="w-6 h-6 text-yellow-600"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                            />
                          </svg>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center p-6 bg-yellow-50 rounded-lg border-2 border-dashed border-yellow-300">
                    <svg
                      className="mx-auto h-8 w-8 text-yellow-400"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"
                      />
                    </svg>
                    <p className="mt-2 text-sm text-yellow-600">
                      No awards listed
                    </p>
                  </div>
                )}
              </div>

              {/* Other Additional Information */}
              {(candidate.hobbies_interests ||
                candidate.volunteer_experience) && (
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 mb-6">
                      Additional Information
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      {candidate.hobbies_interests &&
                        candidate.hobbies_interests.length > 0 && (
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                              Interests & Hobbies
                            </h4>
                            <div className="flex flex-wrap gap-2">
                              {candidate.hobbies_interests.map((hobby, index) => (
                                <span
                                  key={index}
                                  className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-gray-200 text-gray-800"
                                >
                                  {hobby}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}

                      {candidate.volunteer_experience &&
                        candidate.volunteer_experience.length > 0 && (
                          <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                            <h4 className="text-md font-semibold text-gray-900 mb-3">
                              Volunteer Experience
                            </h4>
                            <div className="space-y-2">
                              {candidate.volunteer_experience.map(
                                (vol, index) => (
                                  <div key={index} className="text-sm">
                                    <p className="font-medium text-gray-900">
                                      {typeof vol === "string"
                                        ? vol
                                        : vol.organization}
                                    </p>
                                    {typeof vol === "object" && vol.role && (
                                      <p className="text-gray-600">{vol.role}</p>
                                    )}
                                  </div>
                                ),
                              )}
                            </div>
                          </div>
                        )}
                    </div>
                  </div>
                )}
            </div>
          )}

          {/* Resumes Tab */}
          {/* {activeTab === "resumes" && (
            <div className="space-y-6">
              {candidate.resumes && candidate.resumes.length > 0 ? (
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-6">
                    Uploaded Resumes
                  </h3>
                  <div className="space-y-4">
                    {candidate.resumes.map((resume, index) => (
                      <div
                        key={resume.id}
                        className="border border-gray-200 rounded-lg p-4"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <svg
                              className="h-8 w-8 text-red-500 mr-3"
                              fill="currentColor"
                              viewBox="0 0 20 20"
                            >
                              <path
                                fillRule="evenodd"
                                d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z"
                                clipRule="evenodd"
                              />
                            </svg>
                            <div>
                              <p className="text-sm font-medium text-gray-900">
                                {resume.original_filename}
                              </p>
                              <p className="text-xs text-gray-500">
                                Uploaded:{" "}
                                {new Date(
                                  resume.created_at,
                                ).toLocaleDateString()}
                                {resume.is_primary && (
                                  <span className="ml-2 text-emerald-600 font-medium">
                                    • Primary
                                  </span>
                                )}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span
                              className={`px-2 py-1 text-xs font-medium rounded-full ${resume.status === "completed"
                                  ? "bg-green-100 text-green-800"
                                  : resume.status === "processing"
                                    ? "bg-yellow-100 text-yellow-800"
                                    : resume.status === "failed"
                                      ? "bg-red-100 text-red-800"
                                      : "bg-gray-100 text-gray-800"
                                }`}
                            >
                              {resume.status}
                            </span>
                            {resume.confidence_score && (
                              <span className="text-xs text-gray-500">
                                {Math.round(resume.confidence_score * 100)}%
                                confidence
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center p-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <svg
                    className="mx-auto h-12 w-12 text-gray-400"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">
                    Resume Management
                  </h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Your profile data is managed through the edit profile
                    section. Use the Edit Profile button to update your
                    information.
                  </p>
                  <div className="mt-4">
                    <button
                      onClick={handleEditProfile}
                      className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-emerald-700 bg-emerald-100 hover:bg-emerald-200"
                    >
                      Edit Profile
                    </button>
                  </div>
                </div>
              )}
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default CandidateProfileView;
